import { Vec2 } from '../math/Vec2';
import { Geometry } from '../math/Geometry';
import { Arena } from '../entities/Arena';
import { Paddle } from '../entities/Paddle';
import { Ball } from '../entities/Ball';
import { PowerUp, PowerUpType } from '../entities/PowerUp';
import { BALL, SCORES } from '../config/constants';
import { EventBus } from '../core/EventBus';

export interface CollisionEventData {
  ball: Ball;
  paddle?: Paddle;
  point: Vec2;
  isSmash: boolean;
  isSweetSpot: boolean;
  speed: number;
}

export class CollisionSystem {
  static resolveBallCollisions(
    balls: Ball[],
    paddles: Paddle[],
    arena: Arena,
    onPlayerLoss: (loserIndex: number, ball: Ball) => void
  ): void {
    for (let bIdx = balls.length - 1; bIdx >= 0; bIdx--) {
      const ball = balls[bIdx];
      if (!ball || ball.isDead) continue;

      const numSides = paddles.length;
      if (numSides < 2) break;
      const edges = arena.getAllEdges(numSides);

      const nextBallPos = ball.pos.add(ball.vel);
      let bouncedThisFrame = false;

      for (let i = 0; i < numSides; i++) {
        const pad = paddles[i];
        const edge = edges[i];
        if (!pad || !edge) continue;

        const padPhys = pad.getPhysicalEndpoints(edge.p1, edge.p2, edge.faceAngle);

        // 1. Continuous Collision Detection against Tilted / Smashed Paddle
        const sweepHit = Geometry.sweepCircleSegment(
          ball.pos,
          nextBallPos,
          ball.radius,
          padPhys.start,
          padPhys.end
        );

        if (sweepHit && sweepHit.hit) {
          const dot = ball.vel.dot(sweepHit.normal);

          if (dot < 0) {
            // Reflect velocity
            let newVel = ball.vel.sub(sweepHit.normal.scale(2 * dot));

            // Spin transfer
            const sideLen = edge.p1.distanceTo(edge.p2);
            const paddleSpeedPixels = pad.vel * sideLen;
            ball.applySpin(paddleSpeedPixels);
            if (!pad.isWall) {
              ball.lastHitterId = pad.id;
            }
            bouncedThisFrame = true;

            let isSweetSpot = false;
            let isSmash = false;

            if (!pad.isWall) {
              // Sweet Spot check: within 5% of paddle center (0.45 .. 0.55)
              const hitPrecision = Math.abs(sweepHit.paddleT - 0.5);
              isSweetSpot = hitPrecision < BALL.SWEET_SPOT_THRESHOLD;

              // Smash check
              if (pad.smashActiveTicks > 0 && padPhys.isForwardSmash) {
                isSmash = true;
                ball.normalSpeed = ball.speed / BALL.BOUNCE_SPEED_MULTIPLIER;
                ball.isSmashed = true;
                newVel = newVel.scale(BALL.SMASH_SPEED_MULT);
                ball.color = '#ffffff';
                pad.smashActiveTicks = 0; // Consume smash
              } else if (isSweetSpot) {
                newVel = newVel.scale(BALL.SWEET_SPOT_SPEED_MULT);
                ball.color = '#ffcc00'; // Gold ball for sweet spot
              } else {
                if (ball.isSmashed) {
                  const curSpeed = newVel.length();
                  const targetSpeed = ball.normalSpeed * BALL.BOUNCE_SPEED_MULTIPLIER;
                  newVel = newVel.scale(targetSpeed / (curSpeed || 1));
                  ball.isSmashed = false;
                } else {
                  newVel = newVel.scale(BALL.BOUNCE_SPEED_MULTIPLIER);
                }
                ball.color = ball.isMain ? pad.color : '#ffff00';
              }

              // Anti-stall record
              if (ball.isMain) {
                if (
                  ball.hitHistory.length === 0 ||
                  ball.hitHistory[ball.hitHistory.length - 1] !== pad.id
                ) {
                  ball.hitHistory.push(pad.id);
                  if (ball.hitHistory.length > 5) ball.hitHistory.shift();
                }
              }
            } else {
              // Track wall bounces for anti-stall detection in 1v1 mode
              if (ball.isMain) {
                if (
                  ball.hitHistory.length === 0 ||
                  ball.hitHistory[ball.hitHistory.length - 1] !== pad.id
                ) {
                  ball.hitHistory.push(pad.id);
                  if (ball.hitHistory.length > 5) ball.hitHistory.shift();
                }

                // Wall-stall deflection: if last 4 bounces are only walls,
                // nudge the ball toward center to break the loop
                if (ball.hitHistory.length >= 4) {
                  const recent = ball.hitHistory.slice(-4);
                  const allWalls = recent.every(id => id.startsWith('w'));
                  if (allWalls) {
                    const speed = ball.vel.length();
                    const currentAngle = Math.atan2(ball.vel.y, ball.vel.x);
                    // Nudge angle 30° toward center (vertical axis)
                    const centerAngle = ball.pos.y < arena.center.y ? Math.PI / 2 : -Math.PI / 2;
                    const nudge = 0.5; // blend factor toward center
                    const newAngle = currentAngle + nudge * Math.atan2(
                      Math.sin(centerAngle - currentAngle),
                      Math.cos(centerAngle - currentAngle)
                    );
                    ball.vel = Vec2.fromAngle(newAngle, speed);
                    ball.hitHistory = []; // Reset to avoid repeated nudges
                  }
                }
              }
            }

            // Cap maximum speed
            const postHitSpeed = newVel.length();
            if (postHitSpeed > BALL.MAX_SPEED) {
              newVel = newVel.scale(BALL.MAX_SPEED / postHitSpeed);
            }

            ball.vel = newVel;
            ball.pos = sweepHit.contactPoint.add(sweepHit.normal.scale(ball.radius + 1));

            // Emit bounce event for sound and VFX
            EventBus.emit<CollisionEventData>('ballBounce', {
              ball,
              paddle: pad,
              point: sweepHit.contactPoint,
              isSmash,
              isSweetSpot,
              speed: ball.vel.length(),
            });

            break; // Handled paddle bounce
          }
        }

        // 2. Shield Forcefield check (full edge line)
        if (pad.shieldTimer > 0 && !bouncedThisFrame) {
          const shieldHit = Geometry.sweepCircleSegment(
            ball.pos,
            nextBallPos,
            ball.radius,
            edge.p1,
            edge.p2
          );

          if (shieldHit && shieldHit.hit) {
            const dot = ball.vel.dot(shieldHit.normal);
            if (dot < 0) {
              ball.vel = ball.vel.sub(shieldHit.normal.scale(2 * dot));
              ball.pos = shieldHit.contactPoint.add(shieldHit.normal.scale(ball.radius + 1));
              ball.lastHitterId = pad.id;
              ball.color = pad.color;

              EventBus.emit<CollisionEventData>('ballBounce', {
                ball,
                paddle: pad,
                point: shieldHit.contactPoint,
                isSmash: false,
                isSweetSpot: false,
                speed: ball.vel.length(),
              });
              bouncedThisFrame = true;
              break;
            }
          }
        }
      }

      // 3. Out-of-bounds / Loss Detection
      // If the ball bounced off a paddle or shield this frame, it cannot be out-of-bounds.
      if (bouncedThisFrame || ball.isDead) {
        continue;
      }

      const toBall = ball.pos.sub(arena.center);
      const distFromCenter = toBall.length();
      const inradius = arena.radius * Math.cos(Math.PI / Math.max(3, numSides));

      // Ball cannot be out-of-bounds if it's comfortably inside the inradius or moving inward
      if (distFromCenter < inradius - ball.radius || toBall.dot(ball.vel) <= 0) {
        continue;
      }

      let loserIndex = -1;
      const prevBallPos = ball.pos.sub(ball.vel);

      for (let i = 0; i < numSides; i++) {
        const edge = edges[i];
        if (!edge) continue;
        // Check if the ball's movement segment during this frame crossed the edge outward
        const crossTest = Geometry.segmentSegmentIntersect(prevBallPos, ball.pos, edge.p1, edge.p2);
        if (crossTest && crossTest.hit && ball.vel.dot(edge.normal) < 0) {
          loserIndex = i;
          break;
        }
      }

      // Fallback for fast balls or boundary clipping
      if (loserIndex === -1 && distFromCenter > arena.radius) {
        const exitAngle = Math.atan2(ball.pos.y - arena.center.y, ball.pos.x - arena.center.x);
        const sliceAngle = (Math.PI * 2) / numSides;
        let minDiff = Infinity;
        for (let i = 0; i < numSides; i++) {
          const targetAngle = arena.baseAngle + i * sliceAngle;
          const diff = Math.atan2(
            Math.sin(exitAngle - targetAngle),
            Math.cos(exitAngle - targetAngle)
          );
          if (Math.abs(diff) < minDiff) {
            minDiff = Math.abs(diff);
            loserIndex = i;
          }
        }
      }

      if (loserIndex >= 0) {
        ball.isDead = true;
        ball.vel = Vec2.zero();
        onPlayerLoss(loserIndex, ball);
      }
    }
  }

  static resolvePowerUpCollisions(
    balls: Ball[],
    powerups: PowerUp[],
    paddles: Paddle[]
  ): void {
    for (let pIdx = powerups.length - 1; pIdx >= 0; pIdx--) {
      const pu = powerups[pIdx];
      let hitBall: Ball | null = null;

      for (const b of balls) {
        if (!b.isDead && b.pos.distanceTo(pu.pos) < b.radius + pu.radius) {
          hitBall = b;
          break;
        }
      }

      if (hitBall) {
        const hitter = hitBall.lastHitterId
          ? paddles.find(p => p.id === hitBall!.lastHitterId)
          : undefined;

        EventBus.emit('powerupCollected', { powerup: pu, hitter });

        // Apply effect
        if (hitter) {
          switch (pu.type) {
            case PowerUpType.ONE_UP:
              hitter.heal();
              EventBus.emit('playerHealed', { paddle: hitter, lives: hitter.lives });
              if (hitter.isHuman) {
                EventBus.emit('scoreAdded', {
                  delta: SCORES.POWERUP_1UP,
                  reason: '1-UP! +200',
                  pos: pu.pos,
                });
              }
              break;
            case PowerUpType.MULTIBALL:
              if (hitter.isHuman) {
                EventBus.emit('scoreAdded', {
                  delta: SCORES.POWERUP_MULTIBALL,
                  reason: 'MULTIBALL! +100',
                  pos: pu.pos,
                });
              }
              break;
            case PowerUpType.SHIELD:
              hitter.shieldTimer = 600; // 10 seconds
              if (hitter.isHuman) {
                EventBus.emit('scoreAdded', {
                  delta: SCORES.POWERUP_SHIELD,
                  reason: 'SHIELD! +100',
                  pos: pu.pos,
                });
              }
              break;
            case PowerUpType.EMP_FREEZE:
              for (const other of paddles) {
                if (!other.isWall && other.id !== hitter.id) {
                  other.freezeTimer = 180; // 3 seconds
                }
              }
              if (hitter.isHuman) {
                EventBus.emit('scoreAdded', {
                  delta: SCORES.POWERUP_EMP,
                  reason: 'EMP FREEZE! +150',
                  pos: pu.pos,
                });
              }
              break;
          }
        }

        powerups.splice(pIdx, 1);
      }
    }
  }
}
